// src/components/email/ReplyModal.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { UserMessage } from '../userMessages';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectGroup, SelectItem } from '@/components/ui/select';

type Recipient = {
  email: string;
  name?: string;
};

type ReplyModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
  replyBody: string;
  setReplyBody: (val: string) => void;
  availableContacts: Recipient[];
  onSubmit: (threadId: string, replyBody: string, toRecipients: string[]) => Promise<void>;
  thread: UserMessage ;
  addresses: any[];
  recipients: string[]|undefined;
};

// Component for managing recipient input with search functionality
const RecipientInput: React.FC<{
  staticRecipients: any[];
  recipients: string[];
  onRecipientsChange: (recipients: string[]) => void;
  availableContacts: Recipient[];
  placeholder: string;
}> = ({ staticRecipients, recipients, onRecipientsChange, availableContacts, placeholder }) => {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [filteredContacts, setFilteredContacts] = useState<Recipient[]>([]);

  const addRecipient = (email: string) => {
    if (email && !recipients.includes(email)) {
      onRecipientsChange([...recipients, email]);
    }
    setInputValue('');
  };

  const removeRecipient = (emailToRemove: string) => {
    onRecipientsChange(recipients.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const email = inputValue.trim();
      if (email) {
        addRecipient(email);
      }
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  

  return (
    <div className="space-y-3">
      {/* Original Recipients */}
      {staticRecipients.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Original Recipients:</div>
          <div className="flex flex-wrap gap-1">
            {staticRecipients.map((email, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded"
              >
                {email}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Additional Recipients */}
      {recipients.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-600 mb-1">Additional Recipients:</div>
          <div className="flex flex-wrap gap-1">
            {recipients.map((email, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2 py-1 text-xs bg-blue-50 text-blue-800 border border-blue-200 rounded"
              >
                {email}
                <button
                  onClick={() => removeRecipient(email)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                  type="button"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Add Recipients Input */}
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={e => {
            const val = e.target.value;
            setInputValue(val);
            setFilteredContacts(
              availableContacts.filter(c =>
                c.email.includes(val) || c.name?.toLowerCase().includes(val.toLowerCase())
              )
            );
          }}
          onKeyDown={handleKeyPress}
          className={`text-sm ${!isValidEmail(inputValue) && inputValue ? 'border-red-300' : 'border-gray-300'} focus:border-blue-500 focus:outline-none`}
          placeholder={placeholder}
        />
        {inputValue && filteredContacts.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded border border-gray-300 bg-white shadow-lg">
            {filteredContacts.map(contact => (
              <li
                key={contact.email}
                className="cursor-pointer px-3 py-2 hover:bg-gray-100 text-sm"
                onClick={() => addRecipient(contact.email)}
              >
                <span className="font-medium">{contact.name || contact.email}</span>
                {contact.name && (
                  <span className="text-gray-500 ml-2">&lt;{contact.email}&gt;</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const ReplyModal: React.FC<ReplyModalProps> = ({
  isOpen,
  onRequestClose,
  replyBody,
  setReplyBody,
  availableContacts,
  onSubmit,
  thread,
  addresses,
  recipients,
}) => {
  const [userRecipients, setUserRecipients] = useState<Recipient[]>([]);

console.log("thread in reply modal", recipients);


  if (!thread.id) {
    console.warn('ReplyModal: no thread provided');
    return null;
  }


  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onRequestClose();
        }
      }}
    >
      <DialogContent
        className="outline-none max-w-none"
        overlayClassName="bg-black/30"
        onInteractOutside={(event) => event.preventDefault()}
      >
      <div className="bg-white border border-gray-300 shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-300 bg-gray-50">
          <h3 className="text-base font-medium text-gray-900">Reply: {thread.subject}</h3>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Recipients Section */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recipients</label>
              <div className="border border-gray-300 rounded p-3 bg-gray-50">
                <RecipientInput
                  staticRecipients={recipients || []}
                  recipients={userRecipients.map(r => r.email)}
                  onRecipientsChange={emails =>
                    setUserRecipients(emails.map(email => ({ email } as Recipient)))
                  }
                  availableContacts={addresses as Recipient[]}
                  placeholder="Add additional recipients..."
                />
              </div>
            </div>

            {/* Message Editor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <div className="border border-gray-300 rounded overflow-hidden">
                <ReactQuill
                  theme="snow"
                  value={replyBody}
                  onChange={setReplyBody}
                  style={{ height: '300px' }}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      ['link'],
                      ['clean']
                    ]
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-300 bg-gray-50">
          <Button
            size="sm"
            onClick={() => {
              onSubmit(thread.id, replyBody, userRecipients.map((r: Recipient) => r.email));
              onRequestClose();
            }}
            disabled={replyBody.trim().length === 0}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700"
          >
            Send Reply
          </Button>
          <Button variant="outline" size="sm" onClick={onRequestClose} className="px-4 py-2">
            Cancel
          </Button>
        </div>
      </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReplyModal;
